import {skipToken} from '@reduxjs/toolkit/query';
import {
  Action,
  Card,
  DataStatus,
  EmptyStatus,
  openDialog,
  showNotification,
} from 'platform/components';
import {Hide, Show, VStack} from 'platform/foundation';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {InspectionType} from '@dms/api/core';
import {useGetCustomerV2Query, useGetVehicleCustomerQuery} from '@dms/api/customer';
import {
  useGetVehicleMileageQuery,
  useGetVehicleQuery,
  usePartialUpdateVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetOccupiedSpaceQuery} from '@dms/api/warehouse';
import {
  GetServiceCheckInApiResponse,
  PostServiceCheckInApiArg,
  useGetMetadaServiceCaseQuery,
  useGetServiceCheckInDefaultDriverApiQuery,
  useGetServiceCheckInQuery,
  usePatchServiceCheckInMutation,
  usePostServiceCheckInMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {getCustomerName, handleApiError, useUpdateOdometer} from '@dms/shared';
import {SendAfterSalesEmailForm, SendAfterSalesCustomerSmsForm} from '@dms/shared-aftersales';

import {buildArray, isNotNilOrEmpty, useBoolean} from 'shared/web';

import {CheckInHandOverForm} from '../../../../../components/CheckInHandOverForm/CheckInHandOverForm';
import {CheckInHandOverParameter} from '../../../../../components/CheckInHandOverParameter/CheckInHandOverParameter';
import {EmailSmsNotificationBoundary} from '../../../../../components/EmailSmsNotifications/EmailSmsNotificationBoundary';
import {NotificationCategory} from '../../../../../components/EmailSmsNotifications/types/EmailSmsNotificationForm';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {getTechnicalInspectionValidUntil} from '../../../../../utils/getTechnicalInspectionValidUntil';
import {openMileageValidationDialogIfNeeded} from '../../../components/openMileageValidationDialogIfNeeded';
import {CreateInspectionDropdown} from './CreateInspectionDropdown';
import {Inspection} from './Inspection';

interface CheckInProps {
  vehicleId: string;
  isReadOnly?: boolean;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function CheckIn(props: CheckInProps) {
  const {serviceCaseId: id} = useWorkshopUrl();

  const {
    data: checkIn,
    isLoading: isCheckInLoading,
    isError: isCheckInError,
  } = useGetServiceCheckInQuery({serviceCaseId: id});

  const {
    data: serviceCase,
    isLoading: isServiceCaseLoading,
    isError: iseServiceCaseError,
  } = useGetMetadaServiceCaseQuery({serviceCaseId: id});
  const serviceCaseVehicleId = serviceCase?.vehicleId;

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    isNotNilOrEmpty(serviceCaseVehicleId) ? {vehicleId: serviceCaseVehicleId} : skipToken
  );

  const {data: parkingPlace} = useGetOccupiedSpaceQuery(
    isNotNilOrEmpty(serviceCaseVehicleId)
      ? {recordId: serviceCaseVehicleId, spaceType: 'VEHICLE'}
      : skipToken
  );

  const {data: vehicleCustomers, isLoading: isVehicleCustomersLoading} = useGetVehicleCustomerQuery(
    isNotNil(vehicle) ? {vehicleId: vehicle.id} : skipToken
  );

  const vehicleOperatorCustomerId = vehicleCustomers?.find(
    (vc) => vc?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR'
  )?.customerId;

  const {data: vehicleOperatorCustomer, isLoading: isVehicleOperatorCustomerLoading} =
    useGetCustomerV2Query(
      isNotNilOrEmpty(vehicleOperatorCustomerId)
        ? {customerId: vehicleOperatorCustomerId}
        : skipToken
    );

  const [handleUpdateOdometer] = useUpdateOdometer(serviceCase?.vehicleId);
  const [postServiceCheckIn] = usePostServiceCheckInMutation();
  const [patchServiceCheckIn, {isLoading: isPatchingServiceCheckIn}] =
    usePatchServiceCheckInMutation();
  const [partialUpdateVehicle] = usePartialUpdateVehicleMutation();
  const {data: vehicleMilage, isLoading: isVehicleMileageLoading} = useGetVehicleMileageQuery(
    isNotNilOrEmpty(serviceCaseVehicleId) ? {vehicleId: serviceCaseVehicleId} : skipToken
  );
  const [isEditing, setEditing, cancelEditing] = useBoolean();

  // RTK reports isLoading while uninitialized; a selectFromResult here would blank driver fields.
  const {
    data: checkInDefaultDriver,
    isLoading: isCheckInDefaultDriverLoading,
    isError: isCheckInDefaultDriverError,
  } = useGetServiceCheckInDefaultDriverApiQuery(
    {serviceCaseId: id},
    {skip: isCheckInLoading || isNotNil(checkIn)}
  );

  const inspectionId = checkIn?.inspectionId?.[0];

  const handleSaveCheckIn = async (data: PostServiceCheckInApiArg['body']) => {
    if (isNotNil(data) && inspectionId) {
      data.inspectionId = [inspectionId];
    }

    const technicalInspectionValidUntil = getTechnicalInspectionValidUntil(
      data?.technicalInspection
    );

    if (isNotNil(technicalInspectionValidUntil) && isNotNil(vehicle)) {
      partialUpdateVehicle({
        vehicleId: vehicle.id,
        patchVehicleRequestBody: {
          state: {technicalInspectionValidUntil},
        },
      })
        .unwrap()
        .catch(handleApiError);
    }

    const recordTitle = `${vehicle?.title} - ${getCustomerName(vehicleOperatorCustomer)} (${i18n.t('entity.workshop.labels.checkIn')})`;

    if (checkIn) {
      await patchServiceCheckIn({body: data, serviceCaseId: id})
        .unwrap()
        .then((response) => {
          openMileageValidationDialogIfNeeded(response);

          if (serviceCase && (vehicleMilage?.mileage ?? 0) < (data?.mileage ?? 0)) {
            handleUpdateOdometer({
              recordId: serviceCase.id ?? '',
              milage: data?.mileage ?? 0,
              action: 'service',
              recordTitle,
            });
          }

          cancelEditing();
        })
        .catch(handleApiError);
      return;
    }

    await postServiceCheckIn({body: data, serviceCaseId: id})
      .unwrap()
      .then((response) => {
        openMileageValidationDialogIfNeeded(response);

        cancelEditing();

        handleUpdateOdometer({
          recordId: id,
          milage: data?.mileage ?? 0,
          action: 'service',
          recordTitle,
        });
      })
      .catch(handleApiError);
  };

  const vehicleDefaultValues: GetServiceCheckInApiResponse = {
    technicalInspection: vehicle?.state?.technicalInspectionValidUntil,
  };

  const emptyDefaultValues: GetServiceCheckInApiResponse = {
    driver: {
      id: checkInDefaultDriver?.driverId,
      contactId: checkInDefaultDriver?.driverContactId,
      name: `${checkInDefaultDriver?.driverFirstname ?? ''} ${checkInDefaultDriver?.driverLastname ?? ''}`.trim(),
      email: checkInDefaultDriver?.driverEmail || null,
      phoneNumber: checkInDefaultDriver?.driverPhoneNo
        ? {
            countryCode: checkInDefaultDriver?.driverPhoneCountryCode || '',
            number: checkInDefaultDriver?.driverPhoneNo || '',
            prefix: checkInDefaultDriver?.driverPhonePrefix || '',
          }
        : null,
      identityCard: checkInDefaultDriver?.driverIdentityCard || null,
    },
    consentToTestDrive: true,
    ...vehicleDefaultValues,
  };

  const isLoading =
    isCheckInLoading ||
    isServiceCaseLoading ||
    isVehicleLoading ||
    isCheckInDefaultDriverLoading ||
    isVehicleMileageLoading ||
    isVehicleCustomersLoading ||
    isVehicleOperatorCustomerLoading;
  const isError =
    isCheckInError || iseServiceCaseError || isVehicleError || isCheckInDefaultDriverError;
  const isFormView = !checkIn || checkIn?.state === 'SCHEDULED' || isEditing;

  const cardActions = buildArray<Action>().whenNot(isFormView || props.isReadOnly, {
    type: 'button',
    variant: 'link',
    leftIcon: 'image/edit',
    title: i18n.t('general.actions.edit'),
    onClick: setEditing,
    'data-testid': testIds.workshop.serviceCaseDetail('checkInEdit'),
  });

  const parkingPlaceName =
    `${parkingPlace?.prefix ?? ''}${parkingPlace?.number ?? ''}` || undefined;

  return (
    <VStack spacing={4}>
      <Card title={i18n.t('general.labels.basicInformation')} actions={cardActions}>
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          data-testid={testIds.workshop.serviceCaseDetail('checkInStatus')}
        >
          <Show when={isFormView}>
            <CheckInHandOverForm
              data-testid={testIds.workshop.serviceCaseDetail('checkIneditForm')}
              defaultValues={checkIn ?? emptyDefaultValues}
              fuelType={vehicle?.fuelType}
              onSubmit={handleSaveCheckIn}
              isCheckin
              isEditing={isEditing}
              isReadOnly={props.isReadOnly}
              onEditDiscard={cancelEditing}
            />
          </Show>
          <Hide when={isFormView}>
            <CheckInHandOverParameter
              data={checkIn}
              parkingPlace={parkingPlaceName}
              fuelType={vehicle?.fuelType}
              data-testid={testIds.workshop.serviceCaseDetail('checkInlist')}
              isCheckin
            />
          </Hide>
        </DataStatus>
      </Card>
      <Show when={isNil(inspectionId)}>
        <Card
          title={i18n.t('page.Inspection.labels.title')}
          actions={[
            {
              type: 'custom',
              component: (
                <CreateInspectionDropdown
                  vehicleId={props.vehicleId}
                  serviceCaseTypeId={serviceCase?.serviceCaseTypeId ?? ''}
                  inspectionType={InspectionType.CHECKIN}
                  isDisabled={isNil(checkIn) || props.isReadOnly}
                  isLoading={isPatchingServiceCheckIn}
                  onInspectionCreated={async (inspectionId) => {
                    await patchServiceCheckIn({
                      body: {...checkIn, inspectionId: [inspectionId]},
                      serviceCaseId: id,
                    })
                      .unwrap()
                      .catch(handleApiError);
                  }}
                  data-testid={testIds.workshop.serviceCaseDetail('createCheckinInspection')}
                />
              ),
            },
          ]}
        >
          <EmptyStatus headline={i18n.t('entity.order.actions.checkInEmpty')} />
        </Card>
      </Show>
      <Show when={!!checkIn && !!inspectionId}>
        <Inspection
          inspectionId={inspectionId || ''}
          vehicleId={props.vehicleId}
          isReadOnly={props.isReadOnly}
          isCompletionDisabled={isNil(checkIn?.inspectionId?.[0])}
          data-testid={testIds.workshop.serviceCaseDetail('checkinInspection')}
          onInspectionDeleted={() => {
            if (!checkIn) {
              return;
            }

            const {inspectionId: _, ...rest} = checkIn;

            patchServiceCheckIn({serviceCaseId: id, body: rest}).unwrap().catch(handleApiError);
          }}
          onSendEmailNotification={() => {
            openDialog(
              <SendAfterSalesEmailForm
                serviceCaseId={id}
                defaultValues={{defaultCategory: 'service-customer_check_in'}}
              />,
              {
                id: 'email-sms-notification-dialog',
                withAdditionalFooter: true,
                size: 'large',
                title: i18n.t('general.actions.sendEmail'),
              }
            );
          }}
          onSendSmsNotification={() => {
            if (isNil(serviceCase?.customerId)) {
              showNotification.error(i18n.t('general.notifications.notFound'));
              return;
            }

            openDialog(
              <SendAfterSalesCustomerSmsForm
                serviceCaseId={id}
                resourceId={EntityResourceIds.serviceCase}
                recordId={id}
              />,
              {
                title: i18n.t('general.actions.sendSms'),
                size: 'large',
                scrollBehavior: 'outside',
              }
            );
          }}
          onSendEmailSmsNotification={() => {
            openDialog(
              <EmailSmsNotificationBoundary
                serviceCaseId={id}
                dialogId="email-sms-notification-dialog-checkin"
                data-testid={testIds.workshop.serviceCaseDetail('emailSmsNotificationHandover')}
                defaultCategory={NotificationCategory.CHECKIN}
              />,
              {
                id: 'email-sms-notification-dialog-checkin',
                size: 'large',
                title: i18n.t('entity.order.actions.sendEmailSms'),
              }
            );
          }}
        />
      </Show>
    </VStack>
  );
}
